<script lang="ts" setup>
import {storeToRefs} from "pinia";
import {qrCodeListStore} from "@/scripts/store/qr_code_list.store.js";
import QrCodeBox from "@/scripts/components/app/qrcodes/list/QrCodeBox.vue";
import {ChartBarIcon, PencilSquareIcon, QrCodeIcon, TrashIcon,} from '@heroicons/vue/24/outline'
import {PlusIcon} from "@heroicons/vue/24/solid";
import VLoader from "@/scripts/components/app/common/VLoader.vue";
import {useAccount} from "@/scripts/store/account.ts";
import VWarning from "@/scripts/components/app/common/VWarning.vue";
import InfiniteLoading from "v3-infinite-loading";
import "v3-infinite-loading/lib/style.css";
import {computed, ref} from "vue";
import {PlanType} from "@/scripts/types/account";
import VPlanRequired from "@/scripts/components/app/common/VPlanRequired.vue";

const isFirstLoadingComplete = ref(false)
const selectedAll = ref(false)
const selectedCodes = ref([])

const qrListStore = qrCodeListStore()
const {list, isCompleted} = storeToRefs(qrListStore);
const {account} = storeToRefs(useAccount())

const canCreateQrCode = account.value.statistics.canCreateQrCode

const isAvailableBulkCreate = computed(() => {
    return account.value.currentPlan.label != 'free' as PlanType
})

const selectAll = () => {
    selectedAll.value = !selectedAll.value

    if (selectedAll.value) {
        qrListStore.list.forEach(code => {
            selectedCodes.value.push(code.identifier)
        })
    } else {
        selectedCodes.value = []
    }
}

const deleteSelectedQrCodes = async () => {
    await qrListStore.deleteByIdentifiersList(selectedCodes.value)
}

const deleteQrCode = async (identifier) => {
    if (confirm('Удалить QR код?')) {
        await qrListStore.deleteByIdentifier(identifier)
    }
}

const load = async $state => {

    if (!isCompleted.value) {
        await qrListStore.loadList()
        $state.loaded();

        if (isCompleted.value) {
            $state.complete()
        }
    }

    isFirstLoadingComplete.value = true
};

</script>

<template>
    <div v-if="isFirstLoadingComplete">
        <div class="">
            <div v-if="!canCreateQrCode">
                <v-warning>
                    Исчерпан лимит QR кодов, для создания новых перейдите на <a href="/billing"
                                                                                class="border-bottom border-b-2">следующий
                    тарифный</a> план или удалите один из текущих.
                </v-warning>
            </div>
            <div class="flex sm:flex sm:justify-end mt-4 sm:mt-0" v-if="canCreateQrCode && list.length > 0">
                <div class="sm:ml-16 sm:flex-none mr-2">

                    <button onclick="window.location = '/qrcodes/create'" type="button"
                            class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
                        Создать QR код
                    </button>
                </div>
                <div class="sm:flex-none">
                    <v-plan-required :plan="['business']" :show-lock-label="false">
                        <button onclick="window.location = '/qrcodes/batch_create'" type="button"
                                class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
                            Массовая генерация QR
                        </button>
                    </v-plan-required>
                </div>
            </div>
            <div class="mt-8 flex flex-col">
                <div class="-my-2 -mx-4 sm:-mx-6 lg:-mx-8">
                    <div class="overflow-scroll inline-block min-w-full py-2 align-middle md:px-6 lg:px-8"
                         v-if="list.length > 0">
                        <div class="relative shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <div v-if="selectedCodes.length > 0"
                                 class="absolute top-0 left-12 flex h-12 items-center space-x-3 bg-gray-50 sm:left-16">
                                <button @click="deleteSelectedQrCodes" type="button"
                                        class="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30">
                                    Удалить выбранные
                                </button>
                            </div>
                            <table class="min-w-full table-fixed divide-y divide-gray-300">
                                <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col" class="relative w-12 px-6 sm:w-16 sm:px-8">
                                        <input @click="selectAll" type="checkbox"
                                               class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"/>
                                    </th>
                                    <th scope="col"
                                        class="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 hidden md:table-cell">
                                        QR код
                                    </th>
                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Название
                                    </th>
                                    <th scope="col"
                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell">
                                        Сканирования
                                    </th>
                                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        <span class="sr-only">Редактировать</span>
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-200 bg-white">
                                <tr v-for="code in list" :key="code.identifier"
                                    :class="[selectedCodes.includes(code.identifier) && 'bg-gray-50', 'group']">
                                    <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                                        <div v-if="selectedCodes.includes(code.identifier)"
                                             class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"></div>
                                        <input type="checkbox"
                                               class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                                               :value="code.identifier" v-model="selectedCodes"/>
                                    </td>
                                    <td :class="['whitespace-nowrap py-4 pr-3 text-sm font-medium hidden md:table-cell', selectedCodes.includes(code.identifier) ? 'text-indigo-600' : 'text-gray-900']">
                                        <qr-code-box class="w-24 h-24" :identifier="code.identifier"></qr-code-box>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                        {{ code.title }}
                                        <div class="hidden md:table-cell">
                                            Создан {{ code.createdAt }}
                                        </div>
                                        <div v-if="!code.isActive" class="mt-2">
                                            <span class="inline-flex items-center rounded-full bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">не активен</span>
                                        </div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 hidden md:table-cell">
                                        {{ code.scansCount }}
                                    </td>
                                    <td class="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                        <div class="flex space-x-2 text-gray-400 group-hover:text-gray-800 justify-end">
                                            <a :href="`/qrcodes/${code.identifier}`" class="block">
                                                <PencilSquareIcon
                                                    class="h-6 w-6 hover:text-indigo-900 hover:cursor-pointer"></PencilSquareIcon>
                                            </a>
                                            <a :href="`/qrcodes/${code.identifier}/stats`">
                                                <ChartBarIcon
                                                    class="h-6 w-6 hover:text-indigo-900 hover:cursor-pointer"></ChartBarIcon>
                                            </a>
                                            <a @click="deleteQrCode(code.identifier)">
                                                <TrashIcon
                                                    class="h-6 w-6 hover:text-indigo-900 hover:cursor-pointer"></TrashIcon>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="text-center" v-else>
                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor" aria-hidden="true">
                            <QrCodeIcon class="-ml-1 mr-2 h-5 w-5"></QrCodeIcon>
                        </svg>
                        <h3 class="mt-2 text-sm font-medium text-gray-900">Еще нет QR кодов</h3>
                        <p class="mt-1 text-sm text-gray-500">Начните с создания первого QR кодов</p>
                        <div class="flex mt-6 justify-center">
                            <div>
                                <button onclick="window.location = '/qrcodes/create'" type="button"
                                        class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <PlusIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true"/>
                                    Создать
                                </button>
                            </div>
                            <div v-if="isAvailableBulkCreate" class="ml-2">
                                <button onclick="window.location = '/qrcodes/batch_create'" type="button"
                                        class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
                                    Массовая генерация QR
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="!isFirstLoadingComplete">
        <v-loader class="mt-2"></v-loader>
    </div>
    <InfiniteLoading @infinite="load" class="mt-2 flex">
        <template #complete>
            <div></div>
        </template>
        <template #spinner>
            <div></div>
        </template>
    </InfiniteLoading>
</template>

